import { useEffect, useState, useMemo } from "react";
import MultiSelectField from "../MultiSelectField";
import {
  Box,
  Button,
  Typography,
  Modal,
  IconButton,
  Grid,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import { post } from "../../library/Api";
import { toast } from "react-toastify";

import { PulseLoader } from "react-spinners";

const style = {
  position: "absolute",
  top: "50%",
  right: 10,
  transform: "translateY(-50%)",
  width: 600,
  padding: "30px 30px",
  borderRadius: "10px",
  backgroundColor: "#fff",
  boxShadow:
    "0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)",
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function BasicModal({
  open,
  close,
  admin_id,
  options,
  optionsgroup,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const todayDate = new Date().toISOString().split("T")[0];
  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      group_id: [],
      entry_ids: [],
      entry_datetime: "",
      expiry_datetime: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      group_id: Yup.array().min(0), 
      group_id: Yup.mixed(), 
      entry_ids: Yup.array().when("group_id", {
        is: (val) => {
          
          if (!val) return true;
          if (Array.isArray(val) && val.length === 0) return true;
          return false;
        },
        then: (schema) =>
          schema.min(
            1,
            "At least one entry is required when no group is selected"
          ),
        otherwise: (schema) => schema, 
      }),

      entry_datetime: Yup.date().required("Check-In date is required"),
      expiry_datetime: Yup.date()
        .required("Check-Out date is required")
        .min(Yup.ref("entry_datetime"), "Check-Out must be after Check-In"),
    }),

    onSubmit: async (values) => {
      setIsLoading(true);

      // Map selected group ids → group labels
      const selectedGroupLabels = optionsgroup
        .filter((g) => values.group_id.includes(g.id))
        .map((g) => g.label);

      // Entries chosen manually from Entry list
      const selectedEntries = options.filter((opt) =>
        values.entry_ids.includes(opt.value)
      );

      // Collect all entries from selected groups
      const groupEntries = options.filter((opt) =>
        selectedGroupLabels.includes(opt.group)
      );

      // Final list = group-based entries + manually selected
      const finalEntries = [...groupEntries, ...selectedEntries];

      // Remove duplicates by value
      const uniqueEntries = Array.from(
        new Map(finalEntries.map((e) => [e.value, e])).values()
      );

      // Build entry_ids for payload
      const entry_ids = uniqueEntries.map((e) => e.id ?? e.value);

      const payload = {
        name: values.name,
        email: values.email,
        entry_ids,
        entry_datetime: values.entry_datetime,
        expiry_datetime: values.expiry_datetime,
        admin_id,
      };

      try {
        const response = await post("generatevisitoraccesstoken", payload);
        toast.success("Visitor access links generated successfully!");
        formik.resetForm();
        close();
      } catch (error) {
        toast.error("Something went wrong. Please try again.");
        formik.resetForm();
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    if (open || close) {
      formik.resetForm(); // Reset when modal opens
    }
  }, [open, close]);

  const handleDelete = (valueToDelete) => {
    formik.setFieldValue(
      "entry_ids",
      formik.values.entry_ids.filter((val) => val !== valueToDelete)
    );
  };

  const filteredOptions = useMemo(() => {
    const selectedGroups = optionsgroup
      .filter((g) => formik.values.group_id?.includes(g.id))
      .map((g) => g.label);

    // 0 groups selected → show all entries
    if (selectedGroups.length === 0) {
      return options;
    }

    // 3 groups selected → empty list
    if (selectedGroups.length === 3) {
      return [];
    }

    // 1 group selected → show entries of other 2 groups
    if (selectedGroups.length === 1) {
      return options.filter((opt) => !selectedGroups.includes(opt.group));
    }

    // 2 groups selected → show entries of the remaining group only
    if (selectedGroups.length === 2) {
      const remaining = optionsgroup
        .map((g) => g.label)
        .find((g) => !selectedGroups.includes(g));
      return options.filter((opt) => opt.group === remaining);
    }

    // fallback
    return options;
  }, [options, optionsgroup, formik.values.group_id]);

  return (
    <Modal
      open={open}
      onClose={() => {
        formik.resetForm();
        close();
      }}
      aria-labelledby="visitor-modal-title"
    >
      <Box sx={style}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          {/* <Typography variant="h5">Visitor Pass</Typography> */}
           <h3 className="heading-style">Visitor Pass</h3>
           
          <IconButton onClick={close}>
            <CloseIcon />
          </IconButton>
        </Box>

        {isLoading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: "20%",
            }}
          >
            <PulseLoader color="#02487B" />
            <p style={{ fontWeight: "bold", color: "#02487B", marginTop: 10 }}>
              {/* Loading... */}
            </p>
          </div>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>

              <Grid item xs={12}>
                <MultiSelectField
                  label="Group list"
                  name="group_id"
                  options={optionsgroup.map((g) => ({
                    value: g.id,
                    label: g.label,
                  }))}
                  formik={formik}
                  MenuProps={MenuProps}
                />
              </Grid>

              <Grid item xs={12}>
                <MultiSelectField
                  label="Entry list"
                  name="entry_ids"
                  // options={options.map((e) => ({
                  //   value: e.value,
                  //   label: e.label,
                  // }))}
                  options={filteredOptions}
                  formik={formik}
                  MenuProps={MenuProps}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Check-In Date"
                  name="entry_datetime"
                  type="date"
                  value={formik.values.entry_datetime}
                  onChange={formik.handleChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: todayDate }}
                  error={
                    formik.touched.entry_datetime &&
                    Boolean(formik.errors.entry_datetime)
                  }
                  helperText={
                    formik.touched.entry_datetime &&
                    formik.errors.entry_datetime
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Check-Out Date"
                  name="expiry_datetime"
                  type="date"
                  value={formik.values.expiry_datetime}
                  onChange={formik.handleChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: todayDate }}
                  error={
                    formik.touched.expiry_datetime &&
                    Boolean(formik.errors.expiry_datetime)
                  }
                  helperText={
                    formik.touched.expiry_datetime &&
                    formik.errors.expiry_datetime
                  }
                />
              </Grid>

              <Grid item xs={12} className="text-end">
                <Button variant="contained" color="primary" type="submit">
                  Submit
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </Modal>
  );
}
